import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import type { DungeonQuest } from '@/lib/db/dungeon-types';

interface QuestDesignerModalProps {
  onClose: () => void;
}

const NODE_W = 180;
const NODE_H = 60;
const COL_GAP = 60;
const ROW_GAP = 40;

const TYPE_BORDER_COLOR: Record<DungeonQuest['type'], string> = {
  main: '#eab308',
  optional: '#3b82f6',
  hidden: '#6b7280',
};

const TYPE_LABEL: Record<DungeonQuest['type'], string> = {
  main: 'MAIN',
  optional: 'OPT',
  hidden: 'HID',
};

interface NodeLayout {
  quest: DungeonQuest;
  x: number;
  y: number;
  depth: number;
}

function buildLayout(quests: DungeonQuest[]): NodeLayout[] {
  if (quests.length === 0) return [];

  // Map completionFlag -> questId
  const flagToId = new Map<string, string>();
  for (const q of quests) {
    if (q.completionFlag) {
      flagToId.set(q.completionFlag, q.id);
    }
  }

  // Build adjacency: quest id -> list of quest ids that depend on it
  const depthMap = new Map<string, number>();

  // BFS from roots (quests with no dependsOn that match existing flags)
  const computeDepths = () => {
    // Start all at depth 0
    for (const q of quests) depthMap.set(q.id, 0);

    // Propagate: if quest B depends on quest A, depth(B) >= depth(A) + 1
    let changed = true;
    let iterations = 0;
    while (changed && iterations < quests.length * 2) {
      changed = false;
      iterations++;
      for (const q of quests) {
        for (const dep of q.dependsOn) {
          const parentId = flagToId.get(dep);
          if (parentId && depthMap.has(parentId)) {
            const parentDepth = depthMap.get(parentId)!;
            const myDepth = depthMap.get(q.id)!;
            if (myDepth <= parentDepth) {
              depthMap.set(q.id, parentDepth + 1);
              changed = true;
            }
          }
        }
      }
    }
  };

  computeDepths();

  // Group by depth
  const byDepth = new Map<number, DungeonQuest[]>();
  for (const q of quests) {
    const d = depthMap.get(q.id) ?? 0;
    if (!byDepth.has(d)) byDepth.set(d, []);
    byDepth.get(d)!.push(q);
  }

  // Sort quests within each column by their .order field
  for (const [, group] of byDepth) {
    group.sort((a, b) => a.order - b.order);
  }

  const maxDepth = Math.max(...Array.from(byDepth.keys()));

  // Compute canvas height needed per column
  const layouts: NodeLayout[] = [];
  const PADDING = 40;

  for (let d = 0; d <= maxDepth; d++) {
    const group = byDepth.get(d) ?? [];
    const colX = PADDING + d * (NODE_W + COL_GAP);
    const totalH = group.length * NODE_H + (group.length - 1) * ROW_GAP;
    // center the column vertically; we'll adjust later based on canvas size
    let rowY = PADDING;

    for (const q of group) {
      layouts.push({ quest: q, x: colX, y: rowY, depth: d });
      rowY += NODE_H + ROW_GAP;
    }

    void totalH; // used in height calculation below
  }

  return layouts;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const arrowSize = 7;
  ctx.beginPath();
  ctx.moveTo(x1, y1);

  // Cubic bezier: control points offset horizontally
  const cx1 = x1 + (x2 - x1) * 0.5;
  const cx2 = x2 - (x2 - x1) * 0.5;
  ctx.bezierCurveTo(cx1, y1, cx2, y2, x2, y2);
  ctx.stroke();

  // Arrowhead at (x2, y2)
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - arrowSize * Math.cos(angle - Math.PI / 6),
    y2 - arrowSize * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    x2 - arrowSize * Math.cos(angle + Math.PI / 6),
    y2 - arrowSize * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
}

function renderGraph(
  canvas: HTMLCanvasElement,
  quests: DungeonQuest[]
) {
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;

  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);

  // Background
  ctx.fillStyle = '#0d0d1a';
  ctx.fillRect(0, 0, cssW, cssH);

  if (quests.length === 0) {
    ctx.fillStyle = '#4b5563';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('No quests defined yet.', cssW / 2, cssH / 2);
    return;
  }

  const layouts = buildLayout(quests);

  // Build lookup: questId -> layout
  const idToLayout = new Map<string, NodeLayout>();
  for (const l of layouts) idToLayout.set(l.quest.id, l);

  // Build flag -> questId map
  const flagToId = new Map<string, string>();
  for (const q of quests) {
    if (q.completionFlag) flagToId.set(q.completionFlag, q.id);
  }

  // Draw edges first
  ctx.strokeStyle = '#3b82f6';
  ctx.fillStyle = '#3b82f6';
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.7;

  for (const layout of layouts) {
    for (const dep of layout.quest.dependsOn) {
      const parentId = flagToId.get(dep);
      if (!parentId) continue;
      const parentLayout = idToLayout.get(parentId);
      if (!parentLayout) continue;

      // Exit point: right-center of parent node
      const x1 = parentLayout.x + NODE_W;
      const y1 = parentLayout.y + NODE_H / 2;
      // Entry point: left-center of child node
      const x2 = layout.x;
      const y2 = layout.y + NODE_H / 2;

      drawArrow(ctx, x1, y1, x2, y2);
    }
  }

  ctx.globalAlpha = 1;

  // Draw nodes
  for (const { quest, x, y } of layouts) {
    const borderColor = TYPE_BORDER_COLOR[quest.type];

    // Node background
    ctx.fillStyle = '#1a1a2e';
    drawRoundedRect(ctx, x, y, NODE_W, NODE_H, 6);
    ctx.fill();

    // Colored border
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 2;
    drawRoundedRect(ctx, x, y, NODE_W, NODE_H, 6);
    ctx.stroke();

    // Type label (top-right badge)
    ctx.fillStyle = borderColor;
    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(TYPE_LABEL[quest.type], x + NODE_W - 6, y + 13);

    // Name
    ctx.fillStyle = '#f3f4f6';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'left';
    const maxNameWidth = NODE_W - 12;
    let name = quest.name || 'Unnamed';
    // Truncate if too wide
    while (ctx.measureText(name).width > maxNameWidth && name.length > 1) {
      name = name.slice(0, -1);
    }
    if (name !== quest.name) name = name.slice(0, -1) + '…';
    ctx.fillText(name, x + 8, y + 26);

    // Completion flag
    if (quest.completionFlag) {
      ctx.fillStyle = '#9ca3af';
      ctx.font = '10px monospace';
      ctx.textAlign = 'left';
      let flag = quest.completionFlag;
      while (ctx.measureText(flag).width > maxNameWidth && flag.length > 1) {
        flag = flag.slice(0, -1);
      }
      if (flag !== quest.completionFlag) flag = flag.slice(0, -1) + '…';
      ctx.fillText(flag, x + 8, y + 44);
    }
  }
}

export function QuestDesignerModal({ onClose }: QuestDesignerModalProps) {
  const quests = useDungeonEditorStore((s) => s.mapData.quests);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    renderGraph(canvas, quests);

    // Re-render on resize
    const observer = new ResizeObserver(() => {
      renderGraph(canvas, quests);
    });
    observer.observe(canvas);

    return () => observer.disconnect();
  }, [quests]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0d0d1a]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-950/80">
        <h2 className="text-sm font-semibold text-gray-100">Quest Designer</h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded text-gray-400 hover:text-gray-100 hover:bg-gray-700 transition-colors"
          title="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="flex-1 w-full h-full"
        style={{ display: 'block' }}
      />
    </div>
  );
}
